<template>
  <main id="main">
    <header class="header_page shadow_box slideInDown">
        <p id="title_header_page" class="bold">{{ t('exam_list') }}</p>
        <img id="arrow_back_page" src="@/assets/imgs/back.svg" alt="" @click="$router.go(-1)">
    </header>

    <div class="p-3">
        <div class="bg_white p-4 rounded-4 shadow" style="min-height: 80vh;">
            <div style="height: 3rem" class="d-flex justify-content-evenly align-items-center mb-4">
                <div 
                    class="d-flex align-items-center justify-content-center flex-wrap gap-1 py-3 w-50 cursor-pointer"
                    :class="{ 'border_bottom_theme': activeTab === 'retake' }"
                    @click="activeTab = 'retake'"
                >
                    <p class="bold" :class="activeTab === 'retake' ? 'text_color_theme' : 'text_color_gray'">{{ t('retake_exams') }}</p>
                </div>

                <div style="width: 1px" class="h-100 bg_gray"></div>

                <div 
                    class="d-flex align-items-center justify-content-center flex-wrap gap-1 py-3 w-50 cursor-pointer"
                    :class="{ 'border_bottom_theme': activeTab === 'passed' }"
                    @click="activeTab = 'passed'"
                >
                    <p class="bold" :class="activeTab === 'passed' ? 'text_color_theme' : 'text_color_gray'">{{ t('passed_exams') }}</p>
                </div>
            </div>

            <div v-if="loading" class="text-center mt-5">
                <p>{{ t('loading') }}</p>
            </div>

            <div v-else>
                <div v-if="currentList.length === 0" class="text-center mt-5 text_color_gray">
                    {{ t('empty_list') }}
                </div>

                <div v-else class="d-flex flex-column gap-4">
                    <div v-for="exam in currentList" :key="exam.result_id" class="shadow d-flex flex-column row-gap-3 p-3 rounded-5 bg_white border">
                        <div class="d-flex align-items-center justify-content-between pb-3 column-gap-1" style="border-bottom: 1px solid #dcd7d7">
                            <p class="font_normal bold text_color_theme">
                                {{ exam.lesson_title }} - {{ exam.content_title }} <span v-if="exam.second_title">- {{ exam.second_title }}</span>
                            </p>
                            <p class="font_small bold text_color_gray">
                                {{ exam.created_at_local }}
                            </p>
                        </div>

                        <div class="p-2 d-flex flex-column align-items-center row-gap-4">
                            <div class="w-100 d-flex justify-content-between align-items-center">
                                <div class="d-flex align-items-center gap-2">
                                    <img src="@/assets/imgs/avatar.svg" style="width: 24px">
                                    <span class="font_normal bold">{{ exam.student_name }}</span>
                                    
                                    <div class="chart-icon-container cursor-pointer" @click="openChart(exam)">
                                        <svg width="16" height="16" viewBox="0 0 24 24" fill="none" xmlns="http://www.w3.org/2000/svg">
                                            <path d="M3 3V21H21" stroke="white" stroke-width="2" stroke-linecap="round" stroke-linejoin="round"/>
                                            <path d="M7 17L11 13L15 17L21 7" stroke="white" stroke-width="2" stroke-linecap="round" stroke-linejoin="round"/>
                                        </svg>
                                    </div>
                                </div>

                                <div class="d-flex gap-3">
                                    <div class="position-relative" @click="openSheet(exam)">
                                         <img src="@/assets/imgs/info-sign.png" class="action-icon" :alt="t('view_sheet')">
                                    </div>

                                    <div class="position-relative" @click="openNoteModal(exam)">
                                         <img 
                                            src="@/assets/imgs/text.svg" 
                                            class="action-icon" 
                                            :class="{ 'disabled-icon': exam.is_note === 1 }"
                                            :alt="t('add_note')"
                                         >
                                    </div>
                                </div>
                            </div>

                            <div class="w-100 d-flex justify-content-start column-gap-3 align-items-center">
                                <img src="@/assets/imgs/11-20.svg" style="width: 20px">
                                <span class="bold" :class="exam.is_passed ? 'text_color_theme' : 'text_color_red'">
                                    {{ exam.score }} / {{ exam.total_score }}
                                </span>
                            </div>

                            <div v-if="exam.teachers_note" class="w-100 shadow-sm d-flex flex-column justify-content-start align-items-start bg_box_light_theme p-3 rounded-4">
                                <span class="font_small bold text_color_theme mb-1">{{ t('guide_master') }} :</span>
                                <span class="font_small">{{ exam.teachers_note }}</span>
                            </div>
                        </div>
                    </div>
                </div>
            </div>
        </div>
    </div>

    <div v-if="chartModal.visible" class="modal_overlay" @click.self="chartModal.visible = false">
        <div class="modal_content bg_white rounded-4 p-3 shadow_box w-95" style="max-width: 600px;">
            <div class="d-flex justify-content-between align-items-center mb-3">
                <p class="bold font_medium">{{ t('progress_chart') }}: {{ chartModal.studentName }}</p>
                <img src="@/assets/imgs/close.svg" class="cursor-pointer" style="width: 24px" @click="chartModal.visible = false" alt="">
            </div>
            
            <p class="text_color_theme font_small mb-3 text-center">{{ chartModal.lessonTitle }}</p>

            <div v-if="chartModal.loading" class="text-center py-5">
                <p>{{ t('loading') }}</p>
            </div>
            
            <div v-else-if="!chartModal.data.datasets[0].data.length" class="text-center py-5 text_color_gray">
                {{ t('empty_list') }}
            </div>

            <div v-else class="chart-scroll-container">
                <div class="chart-wrapper" :style="{ width: chartModal.dynamicWidth }">
                    <Line :data="chartModal.data" :options="chartModal.options" />
                </div>
            </div>
        </div>
    </div>

    <div v-if="noteModal.visible" class="modal_overlay" @click.self="noteModal.visible = false">
        <div class="modal_content bg_white rounded-4 p-4 shadow_box w-90" style="max-width: 400px;">
            <p class="bold font_medium mb-3 text-center">{{ t('student_guide') }}</p>
            <textarea 
                v-model="noteModal.text" 
                rows="5" 
                class="w-100 p-3 rounded-4 border_gray mb-3" 
                :placeholder="t('write_explanation')"
            ></textarea>
            <div class="d-flex gap-2">
                <button class="button w-100" @click="submitNote" :disabled="noteModal.loading">
                    {{ noteModal.loading ? t('loading') : t('submit_post') }}
                </button>
                <button class="button w-100 bg_gray" @click="noteModal.visible = false" :disabled="noteModal.loading">{{ t('cancel') }}</button>
            </div>
        </div>
    </div>

    <div v-if="sheetModal.visible" class="modal_overlay" @click.self="sheetModal.visible = false">
        <div class="modal_content bg_white rounded-4 p-0 shadow_box w-95 h-85" style="max-width: 600px; overflow: hidden; display: flex; flex-direction: column;">
            <div class="p-3 border-bottom d-flex justify-content-between align-items-center bg_light_theme">
                <p class="text_white bold">{{ t('sheet_title') }}</p>
                <img src="@/assets/imgs/close.svg" class="cursor-pointer filter-white" style="width: 24px" @click="sheetModal.visible = false" alt="">
            </div>
            
            <div class="flex-grow-1 overflow-auto p-3">
                <div v-if="sheetModal.loading" class="text-center py-5">
                    <p>{{ t('loading') }}</p>
                </div>
                <div v-else>
                    <div class="d-flex justify-content-between mb-3 px-2">
                        <span class="font_small bold">{{ t('score') }}: {{ sheetModal.data.score }} / {{ sheetModal.data.total_score }}</span>
                        <span class="font_small bold" :class="sheetModal.data.is_passed ? 'text_color_theme' : 'text_color_red'">
                            {{ sheetModal.data.is_passed ? t('passed') : t('failed') }}
                        </span>
                    </div>

                    <div v-for="(q, index) in sheetModal.data.questions" :key="index" class="mb-4 p-3 border rounded-4 bg_gray_light">
                        <p class="bold mb-3 font_normal">{{ index + 1 }}. {{ q.question }}</p>
                        
                        <div class="d-flex flex-column gap-2">
                            <div 
                                v-for="(optText, optKey) in q.options" 
                                :key="optKey"
                                class="p-2 rounded-3 border d-flex justify-content-between align-items-center"
                                :class="{
                                    'bg-success-light border-success': q.correct_answer_key === optKey,
                                    'bg-danger-light border-danger': q.student_answer_key === optKey && q.student_answer_key !== q.correct_answer_key,
                                    'bg_white': q.student_answer_key !== optKey && q.correct_answer_key !== optKey
                                }"
                            >
                                <span class="font_small">{{ optKey }}) {{ optText }}</span>
                                <img v-if="q.correct_answer_key === optKey" src="@/assets/imgs/tick.svg" style="width: 16px;">
                                <img v-if="q.student_answer_key === optKey && q.student_answer_key !== q.correct_answer_key" src="@/assets/imgs/close.svg" style="width: 16px;">
                            </div>
                        </div>
                    </div>
                </div>
            </div>
        </div>
    </div>

  </main>
</template>

<script>
import apiClient, { createFormData } from '@/services/api'
import { Line } from 'vue-chartjs'
import { Chart as ChartJS, CategoryScale, LinearScale, PointElement, LineElement, Title, Tooltip, Legend } from 'chart.js'

ChartJS.register(CategoryScale, LinearScale, PointElement, LineElement, Title, Tooltip, Legend)

export default {
  name: 'ExamList',
  components: { Line },
  data() {
    return {
      activeTab: 'retake',
      loading: false,
      passedExams: [],
      retakeExams: [],
      chartModal: {
        visible: false,
        loading: false,
        studentName: '',
        lessonTitle: '',
        dynamicWidth: '100%',
        data: {
            labels: [],
            datasets: [{
                label: 'نمره',
                backgroundColor: '#4A00E0',
                borderColor: '#4A00E0',
                data: [],
                tension: 0.4,
                fill: false,
            }]
        },
        options: {
            responsive: true,
            maintainAspectRatio: false,
            plugins: { legend: { display: false } },
            scales: {
                x: { ticks: { maxRotation: 90, minRotation: 90, autoSkip: false, font: { size: 10 } } },
                y: { beginAtZero: true, max: 100 }
            },
            elements: { point: { radius: 5, hoverRadius: 7, backgroundColor: '#4A00E0' } },
            layout: { padding: { bottom: 10 } }
        }
      },
      noteModal: {
        visible: false,
        loading: false,
        text: '',
        exam: null
      },
      sheetModal: {
        visible: false,
        loading: false,
        data: {}
      }
    }
  },
  computed: {
    currentList() {
        return this.activeTab === 'passed' ? this.passedExams : this.retakeExams;
    }
  },
  mounted() {
    this.fetchExams();
  },
  methods: {
    async fetchExams() {
        this.loading = true;
        try {
            const userId = localStorage.getItem('user_id');
            const formData = createFormData({
                teacher_id: userId
            });

            const response = await apiClient.post('/teacher/exams_list', formData);

            if (response.data.status === 'ok' && response.data.data) {
                this.passedExams = response.data.data.passed_exams || [];
                this.retakeExams = response.data.data.retake_exams || [];
            }
        } catch (error) {
            console.error("Error fetching exams:", error);
        } finally {
            this.loading = false;
        }
    },
    async openChart(exam) {
        this.chartModal.visible = true;
        this.chartModal.loading = true;
        this.chartModal.studentName = exam.student_name;
        this.chartModal.lessonTitle = exam.lesson_title;
        this.chartModal.data.labels = [];
        this.chartModal.data.datasets[0].data = [];

        try {
            const userId = localStorage.getItem('user_id');
            const formData = createFormData({
                teacher_id: userId,
                lesson_id: exam.lesson_id,
                user_id: exam.student_id
            });

            const response = await apiClient.post('/teacher/chart_lessons_details', formData);

            if (response.data.status === 'ok' && response.data.data) {
                const points = response.data.data.points || [];
                this.chartModal.data.labels = points.map(p => p.date);
                this.chartModal.data.datasets[0].data = points.map(p => p.score);

                const widthPerPoint = 50;
                const calculatedWidth = points.length * widthPerPoint;
                this.chartModal.dynamicWidth = calculatedWidth > window.innerWidth ? `${calculatedWidth}px` : '100%';
            }
        } catch (error) {
            console.error(error);
        } finally {
            this.chartModal.loading = false;
        }
    },
    openNoteModal(exam) {
        if (exam.is_note === 1) return; 
        this.noteModal.exam = exam;
        this.noteModal.text = '';
        this.noteModal.visible = true;
    },
    async submitNote() {
        if (!this.noteModal.text.trim()) {
            this.notify(this.t('write_explanation'), 'error');
            return;
        }
        
        this.noteModal.loading = true;
        try {
            const userId = localStorage.getItem('user_id');
            const formData = createFormData({
                teacher_id: userId,
                result_id: this.noteModal.exam.result_id,
                note: this.noteModal.text
            });

            const response = await apiClient.post('/teacher/add_exams_note', formData);

            if (response.data.status === 'ok') {
                this.notify(response.data.message, 'success');
                
                if (this.noteModal.exam) {
                    this.noteModal.exam.is_note = 1;
                    this.noteModal.exam.teachers_note = this.noteModal.text;
                }
                this.noteModal.visible = false;
            } else {
                this.notify(response.data.message || this.t('error_submit'), 'error');
            }
        } catch (error) {
            console.error(error);
            this.notify(this.t('connection_error'), 'error');
        } finally {
            this.noteModal.loading = false;
        }
    },
    async openSheet(exam) {
        this.sheetModal.visible = true;
        this.sheetModal.loading = true;
        this.sheetModal.data = {};

        try {
            const userId = localStorage.getItem('user_id');
            const formData = createFormData({
                teacher_id: userId,
                result_id: exam.result_id
            });

            const response = await apiClient.post('/teacher/show_exams_sheet', formData);

            if (response.data.status === 'ok' && response.data.data) {
                this.sheetModal.data = response.data.data;
            }
        } catch (error) {
            console.error(error);
            this.notify(this.t('connection_error'), 'error');
            this.sheetModal.visible = false;
        } finally {
            this.sheetModal.loading = false;
        }
    }
  }
}
</script>

<style scoped>
.chart-icon-container {
    width: 28px;
    height: 28px;
    background: linear-gradient(135deg, #667eea 0%, #764ba2 100%);
    border-radius: 50%;
    display: flex;
    justify-content: center;
    align-items: center;
    box-shadow: 0 2px 5px rgba(0,0,0,0.2);
    transition: transform 0.2s;
}
.chart-icon-container:active {
    transform: scale(0.9);
}
.cursor-pointer {
    cursor: pointer;
}
.modal_overlay {
  position: fixed;
  top: 0;
  left: 0;
  width: 100%;
  height: 100vh;
  background-color: rgba(0, 0, 0, 0.5);
  backdrop-filter: blur(5px);
  z-index: 2000;
  display: flex;
  justify-content: center;
  align-items: center;
}
.chart-scroll-container {
  width: 100%;
  overflow-x: auto;
  overflow-y: hidden;
  -webkit-overflow-scrolling: touch; 
}
.chart-wrapper {
  height: 350px;
  position: relative;
}
.action-icon {
    width: 24px;
    height: 24px;
    cursor: pointer;
    transition: transform 0.2s;
}
.action-icon:hover {
    transform: scale(1.1);
}
.disabled-icon {
    opacity: 0.3;
    filter: grayscale(100%);
    cursor: default;
    pointer-events: none;
}
.border_gray {
    border: 1px solid #ddd;
}
.bg_gray_light {
    background-color: #f9f9f9;
}
.bg-success-light {
    background-color: #d4edda;
}
.bg-danger-light {
    background-color: #f8d7da;
}
.text_white {
    color: white;
}
.filter-white {
    filter: brightness(0) invert(1);
}
.h-85 {
    height: 85vh;
}
</style>